import type { ReactNode } from 'react';

import { cn } from '@/shared/cn';

/**
 * Dotted-border empty-state card used across settings + admin surfaces —
 * "No connectors yet" / "No matches" / "Nothing saved yet". The dotted
 * outline (zinc-300 light / zinc-700 dark) signals "nothing here yet" and
 * is deliberately distinct from the divider vocabulary — dividers went
 * solid in the 2026-06-12 call (`--color-divider`, ui-class.ts); this
 * outline is the one surviving dotted treatment. Glass surface, centered
 * icon, title, optional description, optional CTA.
 */
export type EmptyStateProps = {
  /** A pre-styled Lucide icon, typically `h-8 w-8 text-zinc-400`. */
  icon: ReactNode;
  title: string;
  description?: ReactNode;
  /** Optional CTA — pass a styled `<Link>` or `<button>` element. */
  action?: ReactNode;
  className?: string;
  /**
   * Drop the standalone card chrome (dotted border + glass surface + blur)
   * for use *inside* an opaque container — e.g. an `AdminSectionCard` or a
   * table body, where a second bordered/glass card reads as redundant
   * double-chrome (and glass is off-limits in admin form interiors).
   */
  embedded?: boolean;
};

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  embedded = false,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        embedded
          ? 'p-8 text-center'
          : 'rounded-2xl border border-dotted border-zinc-300 bg-[var(--glass-surface)] p-12 text-center backdrop-blur-lg dark:border-zinc-700',
        className,
      )}
    >
      <div className="mx-auto">{icon}</div>
      <p className="mt-3 font-medium">{title}</p>
      {description ? (
        <p className="mt-1 text-sm text-muted">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-4 inline-flex">{action}</div> : null}
    </div>
  );
}
